import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from 'styles/components/template.module.scss'
import SubmitButton from 'components/Button/SubmitButton'
import Logo from 'components/Icons/Logo'
import RadioButton from 'components/Form/RadioButton'
import {
  Elements,
  useElements,
  useStripe,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'reducks/reducers'
import { getShopById } from 'reducks/shop/slice'
import { auth } from 'fb/index'
import { setIsLoading } from 'reducks/loading/slice'
import * as subscriptionsApi from 'api/subscriptionsApi'
import { CardSecurityCode } from 'images/CardSecurityCode'
import { getShopSubscriptionById } from 'reducks/shopSubscriptions/slice'
import { signOut } from 'firebase/auth'
import paymentImage from 'images/payment-image.png'
import paymentImageReverse from 'images/payment-image-reverse.png'
import checkIcon from 'images/check-icon.png'
import checkInactiveIcon from 'images/check-inactive-icon.png'

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    'pk_test_1jzDEjKdh7nGxRYba6xsRfy600Hi4teGle',
)

const RegisterPlanTemplate: FC = () => {
  const dispatch = useDispatch<any>()
  const location = useLocation()
  const path = location.pathname
  const search = useLocation().search
  const query = new URLSearchParams(search)
  const [selectedPlan, setSelectedPlan] = useState<
    'standard' | 'self' | 'liteMonth' | 'liteYear'
  >('standard')
  const [email, setEmail] = useState('')
  const [cardData, setCardData] = useState('')
  const [isDone, setIsDone] = useState(false)
  const [failedMsg, setFailedMsg] = useState('')
  const [isModal, setIsModal] = useState(false)
  const navigate = useNavigate()
  const { staff } = useSelector((state: RootState) => state.staffSlice)
  const dispatchGetShop = useCallback(
    async (shopId: string) => {
      const token = await auth.currentUser?.getIdToken()
      const shop = await dispatch(getShopById({ id: shopId }))
      // カード情報取得
      if (shop.payload.stripe_payment_method_id) {
        axios
          .post(
            `${process.env.REACT_APP_FUNCTIONS_URL}/apiRetrieveCard`,
            {
              payment_method_id: shop.payload.stripe_payment_method_id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Access-Control-Allow-Origin': '*',
              },
            },
          )
          .then((result) => {
            if (
              !!result.data.card.last4 &&
              !!result.data.card.exp_year &&
              !!shop.payload.stripe_payment_method_id &&
              !!shop.payload.stripe_customer_id
            ) {
              setCardData(
                `**** **** **** ${result.data.card.last4}|${result.data.card.exp_year}/${result.data.card.exp_month}|${shop.payload.stripe_payment_method_id}|${shop.payload.stripe_customer_id}`,
              )
            }
          })
          .catch((error) => {
            console.log(error)
          })
          .finally(() => {
            dispatch(setIsLoading({ isLoading: false }))
          })
      } else {
        dispatch(setIsLoading({ isLoading: false }))
      }
    },
    [dispatch],
  )
  const apiCreateStripeSubscriptionDaseruno = useCallback(
    async (
      email: string,
      paymentMethodId: string,
      plan: string,
      paymentCustomerId: string,
      shopId?: string | null,
      storeId?: string | null,
    ) => {
      await dispatch(setIsLoading({ isLoading: true }))
      /** Subscription作成(既存カード) */
      // デバッグモードの場合は即時課金される
      const isDebug =
        process.env.REACT_APP_NODE_ENV === 'development' && !!query.get('debug')
      subscriptionsApi
        .createSubscription({
          email,
          paymentMethodId,
          plan,
          shopId,
          storeId,
          paymentCustomerId,
          isStandard:
            plan === 'standardMonth' || plan === 'selfMonth' ? true : false,
          isDebug,
        })
        .then(async (result: { data: any }) => {
          console.log(result.data)
          setIsDone(true)
        })
        .catch((error) => {
          setFailedMsg(error.message)
          console.log(error.message)
        })
        .finally(() => dispatch(setIsLoading({ isLoading: false })))
    },
    [dispatch],
  )
  const checkIsRegister = useCallback(
    async (storeId: string | null, isAdmin: boolean) => {
      if (!storeId) return
      const shopSubscription = await dispatch(
        getShopSubscriptionById({
          storeId: storeId,
        }),
      )
      if (
        !!shopSubscription.payload?.stripe_daseruno_subscription_id ||
        !!shopSubscription.payload?.stripe_daseruno_subscription_schedule_id ||
        isAdmin
      ) {
        navigate('/')
      }
    },
    [dispatch, navigate],
  )
  const handleSignOut = useCallback(() => {
    signOut(auth).then(() => {
      navigate('/login')
    })
  }, [navigate])
  useEffect(() => {
    if (!staff) {
      const addPath = query.get('debug')
        ? path + '?debug=' + query.get('debug')
        : path
      navigate('/initload?path=' + addPath)
      window.scrollTo(0, 0)
      return
    }
    if (staff?.shopId) {
      checkIsRegister(staff.storeId, staff.isAdmin) // 登録済みの場合はリダイレクト
      dispatch(setIsLoading({ isLoading: true }))
      dispatchGetShop(staff.shopId)
      setEmail(staff.email)
    }
  }, [staff])
  return (
    <div className={styles.registerplan_new_container}>
      <div className={styles.registerplan_logo}>
        <Logo width={200} />
      </div>

      <h1 className={styles.registerplan_title}>お支払い方法登録</h1>

      <div className={styles.registerplan_plans_container}>
        <div className={styles.registerplan_plan_cards}>
          {/* Debug mode lite plans */}
          {process.env.REACT_APP_NODE_ENV === 'development' &&
          query.get('debug') ? (
            <>
              {/* Lite Monthly Plan */}
              <div
                className={`${styles.registerplan_plan_card} ${
                  selectedPlan === 'liteMonth'
                    ? styles.registerplan_plan_card_selected
                    : ''
                }`}
                onClick={() => setSelectedPlan('liteMonth')}
              >
                <div className={styles.registerplan_plan_header}>
                  <span
                    className={`${styles.registerplan_plan_label} ${styles.registerplan_plan_label_lite}`}
                  >
                    LITE PLAN (月々)
                  </span>
                  <h2 className={styles.registerplan_plan_name}>
                    月々ライトプラン
                  </h2>
                  <p className={styles.registerplan_plan_description}>
                    月々で分割払いしたい方にオススメ！
                  </p>
                </div>

                <div className={styles.registerplan_plan_price}>
                  <span className={styles.registerplan_price_value}>9,800</span>
                  <span className={styles.registerplan_price_unit}>
                    円／月（税込）
                  </span>
                </div>

                <p className={styles.registerplan_plan_note}>
                  年合計：117,600円（税込）
                </p>

                <div className={styles.registerplan_plan_selector}>
                  <RadioButton
                    isChecked={selectedPlan === 'liteMonth'}
                    label=""
                    onChange={() => setSelectedPlan('liteMonth')}
                  />
                </div>
              </div>

              {/* Lite Yearly Plan */}
              <div
                className={`${styles.registerplan_plan_card} ${
                  selectedPlan === 'liteYear'
                    ? styles.registerplan_plan_card_selected
                    : ''
                }`}
                onClick={() => setSelectedPlan('liteYear')}
              >
                <div className={styles.registerplan_plan_header}>
                  <span
                    className={`${styles.registerplan_plan_label} ${styles.registerplan_plan_label_lite}`}
                  >
                    LITE PLAN (年割)
                  </span>
                  <h2 className={styles.registerplan_plan_name}>
                    年割ライトプラン
                  </h2>
                  <p className={styles.registerplan_plan_description}>
                    12ヶ月分をまとめてのお支払い
                  </p>
                </div>

                <div className={styles.registerplan_plan_price}>
                  <span className={styles.registerplan_price_value}>8,980</span>
                  <span className={styles.registerplan_price_unit}>
                    円／月（税込）
                  </span>
                </div>

                <p className={styles.registerplan_plan_note}>
                  一括：107,760円（税込）
                </p>
                <p className={styles.registerplan_plan_note_important}>
                  ※12ヶ月分をまとめてのお支払いとなります。
                </p>

                <div className={styles.registerplan_plan_circle}>
                  1ヶ月分
                  <br />
                  お得！
                </div>

                <div className={styles.registerplan_plan_selector}>
                  <RadioButton
                    isChecked={selectedPlan === 'liteYear'}
                    label=""
                    onChange={() => setSelectedPlan('liteYear')}
                  />
                </div>
              </div>

              {/* Standard Plan Card (Debug Mode) */}
              <div>
                <div className={styles.registerplan_plan_image}>
                  <img src={paymentImage} alt="Standard Plan" />
                </div>
                <div
                  className={`${styles.registerplan_plan_card} ${
                    selectedPlan === 'standard'
                      ? styles.registerplan_plan_card_selected
                      : ''
                  }`}
                  onClick={() => setSelectedPlan('standard')}
                >
                  <div className={styles.registerplan_plan_header}>
                    <span className={styles.registerplan_plan_label}>
                      STANDARD PLAN
                    </span>
                    <h2 className={styles.registerplan_plan_name}>
                      スタンダードプラン
                    </h2>
                    <p className={styles.registerplan_plan_description}>
                      毎日忙しく、時間を短く使いたい店舗様向け
                    </p>
                  </div>

                  <div className={styles.registerplan_plan_price}>
                    <span className={styles.registerplan_price_value}>
                      14,800
                    </span>
                    <span className={styles.registerplan_price_unit}>
                      円／月（税別）
                    </span>
                    <div className={styles.registerplan_price_tax_included}>
                      税込 16,280円
                    </div>
                  </div>

                  <p className={styles.registerplan_plan_note}>
                    初期費用：30,000円（税別）初回のみ発生いたします。
                    <br />
                    <span className={styles.registerplan_tax_included_note}>
                      税込 33,000円
                    </span>
                  </p>
                  <p className={styles.registerplan_plan_note_important}>
                    ※契約期間は最低1年間となります。
                  </p>

                  <div className={styles.registerplan_feature_list_container}>
                    <ul className={styles.registerplan_feature_list}>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>売価サジェスト機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>原価管理機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ管理機能</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_highlight}`}
                      >
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ登録代行</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_highlight}`}
                      >
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>請求書取り込み代行</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>栄養成分値算出機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>裏貼シール出力機能</span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.registerplan_plan_selector}>
                    <RadioButton
                      isChecked={selectedPlan === 'standard'}
                      label=""
                      onChange={() => setSelectedPlan('standard')}
                    />
                  </div>
                </div>
              </div>

              {/* Self Plan Card (Debug Mode) */}
              <div>
                <div className={styles.registerplan_plan_image}>
                  <img src={paymentImageReverse} alt="Self Plan" />
                </div>
                <div
                  className={`${styles.registerplan_plan_card} ${
                    selectedPlan === 'self'
                      ? styles.registerplan_plan_card_selected
                      : ''
                  }`}
                  onClick={() => setSelectedPlan('self')}
                >
                  <div className={styles.registerplan_plan_header}>
                    <span
                      className={`${styles.registerplan_plan_label} ${styles.registerplan_plan_label_self}`}
                    >
                      SELF PLAN
                    </span>
                    <h2 className={styles.registerplan_plan_name}>
                      セルフプラン
                    </h2>
                    <p className={styles.registerplan_plan_description}>
                      自分で入力・登録を行いたい店舗様向け
                    </p>
                  </div>

                  <div className={styles.registerplan_plan_price}>
                    <span className={styles.registerplan_price_value}>
                      4,800
                    </span>
                    <span className={styles.registerplan_price_unit}>
                      円／月（税別）
                    </span>
                    <div className={styles.registerplan_price_tax_included}>
                      税込 5,280円
                    </div>
                  </div>

                  <p className={styles.registerplan_plan_note}>
                    初期費用：15,000円（税別）初回のみ発生いたします。
                    <br />
                    <span className={styles.registerplan_tax_included_note}>
                      税込 16,500円
                    </span>
                  </p>
                  <p className={styles.registerplan_plan_note_important}>
                    ※契約期間は最低1年間となります。
                  </p>

                  <div className={styles.registerplan_feature_list_container}>
                    <ul className={styles.registerplan_feature_list}>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>売価サジェスト機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>原価管理機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ管理機能</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_disabled}`}
                        style={{ opacity: 0.4 }}
                      >
                        <span
                          className={`${styles.registerplan_feature_check} ${styles.registerplan_feature_disabled}`}
                        >
                          <img
                            src={checkInactiveIcon}
                            alt="check inactive"
                            style={{ opacity: 0.4 }}
                          />
                        </span>
                        <span style={{ opacity: 0.4 }}>レシピ登録代行</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_disabled}`}
                        style={{ opacity: 0.4 }}
                      >
                        <span
                          className={`${styles.registerplan_feature_check} ${styles.registerplan_feature_disabled}`}
                        >
                          <img
                            src={checkInactiveIcon}
                            alt="check inactive"
                            style={{ opacity: 0.4 }}
                          />
                        </span>
                        <span style={{ opacity: 0.4 }}>請求書取り込み代行</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>栄養成分値算出機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>裏貼シール出力機能</span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.registerplan_plan_selector}>
                    <RadioButton
                      isChecked={selectedPlan === 'self'}
                      label=""
                      onChange={() => setSelectedPlan('self')}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Standard Plan Card */}
              <div>
                <div className={styles.registerplan_plan_image}>
                  <img src={paymentImage} alt="Standard Plan" />
                </div>
                <div
                  className={`${styles.registerplan_plan_card} ${
                    selectedPlan === 'standard'
                      ? styles.registerplan_plan_card_selected
                      : ''
                  }`}
                  onClick={() => setSelectedPlan('standard')}
                >
                  <div className={styles.registerplan_plan_header}>
                    <span className={styles.registerplan_plan_label}>
                      STANDARD PLAN
                    </span>
                    <h2 className={styles.registerplan_plan_name}>
                      スタンダードプラン
                    </h2>
                    <p className={styles.registerplan_plan_description}>
                      毎日忙しく、時間を短く使いたい店舗様向け
                    </p>
                  </div>

                  <div className={styles.registerplan_plan_price}>
                    <span className={styles.registerplan_price_value}>
                      14,800
                    </span>
                    <span className={styles.registerplan_price_unit}>
                      円／月（税別）
                    </span>
                    <div className={styles.registerplan_price_tax_included}>
                      税込 16,280円
                    </div>
                  </div>

                  <p className={styles.registerplan_plan_note}>
                    初期費用：30,000円（税別）初回のみ発生いたします。
                    <br />
                    <span className={styles.registerplan_tax_included_note}>
                      税込 33,000円
                    </span>
                  </p>
                  <p className={styles.registerplan_plan_note_important}>
                    ※契約期間は最低1年間となります。
                  </p>

                  <div className={styles.registerplan_feature_list_container}>
                    <ul className={styles.registerplan_feature_list}>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>売価サジェスト機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>原価管理機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ管理機能</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_highlight}`}
                      >
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ登録代行</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_highlight}`}
                      >
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>請求書取り込み代行</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>栄養成分値算出機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>裏貼シール出力機能</span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.registerplan_plan_selector}>
                    <RadioButton
                      isChecked={selectedPlan === 'standard'}
                      label=""
                      onChange={() => setSelectedPlan('standard')}
                    />
                  </div>
                </div>
              </div>

              {/* Self Plan Card */}
              <div>
                <div className={styles.registerplan_plan_image}>
                  <img src={paymentImageReverse} alt="Self Plan" />
                </div>
                <div
                  className={`${styles.registerplan_plan_card} ${
                    selectedPlan === 'self'
                      ? styles.registerplan_plan_card_selected
                      : ''
                  }`}
                  onClick={() => setSelectedPlan('self')}
                >
                  <div className={styles.registerplan_plan_header}>
                    <span
                      className={`${styles.registerplan_plan_label} ${styles.registerplan_plan_label_self}`}
                    >
                      SELF PLAN
                    </span>
                    <h2 className={styles.registerplan_plan_name}>
                      セルフプラン
                    </h2>
                    <p className={styles.registerplan_plan_description}>
                      自分で入力・登録を行いたい店舗様向け
                    </p>
                  </div>

                  <div className={styles.registerplan_plan_price}>
                    <span className={styles.registerplan_price_value}>
                      4,800
                    </span>
                    <span className={styles.registerplan_price_unit}>
                      円／月（税別）
                    </span>
                    <div className={styles.registerplan_price_tax_included}>
                      税込 5,280円
                    </div>
                  </div>

                  <p className={styles.registerplan_plan_note}>
                    初期費用：15,000円（税別）初回のみ発生いたします。
                    <br />
                    <span className={styles.registerplan_tax_included_note}>
                      税込 16,500円
                    </span>
                  </p>
                  <p className={styles.registerplan_plan_note_important}>
                    ※契約期間は最低1年間となります。
                  </p>

                  <div className={styles.registerplan_feature_list_container}>
                    <ul className={styles.registerplan_feature_list}>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>売価サジェスト機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>原価管理機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>レシピ管理機能</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_disabled}`}
                        style={{ opacity: 0.4 }}
                      >
                        <span
                          className={`${styles.registerplan_feature_check} ${styles.registerplan_feature_disabled}`}
                        >
                          <img
                            src={checkInactiveIcon}
                            alt="check inactive"
                            style={{ opacity: 0.4 }}
                          />
                        </span>
                        <span style={{ opacity: 0.4 }}>レシピ登録代行</span>
                      </li>
                      <li
                        className={`${styles.registerplan_feature_item} ${styles.registerplan_feature_disabled}`}
                        style={{ opacity: 0.4 }}
                      >
                        <span
                          className={`${styles.registerplan_feature_check} ${styles.registerplan_feature_disabled}`}
                        >
                          <img
                            src={checkInactiveIcon}
                            alt="check inactive"
                            style={{ opacity: 0.4 }}
                          />
                        </span>
                        <span style={{ opacity: 0.4 }}>請求書取り込み代行</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>栄養成分値算出機能</span>
                      </li>
                      <li className={styles.registerplan_feature_item}>
                        <span className={styles.registerplan_feature_check}>
                          <img src={checkIcon} alt="check" />
                        </span>
                        <span>裏貼シール出力機能</span>
                      </li>
                    </ul>
                  </div>

                  <div className={styles.registerplan_plan_selector}>
                    <RadioButton
                      isChecked={selectedPlan === 'self'}
                      label=""
                      onChange={() => setSelectedPlan('self')}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Payment Section */}
      <div className={styles.registerplan_payment_section}>
        <h2 className={styles.registerplan_payment_title}>お支払い方法登録</h2>
        <p className={styles.registerplan_payment_subtitle}>
          月額基本利用料のお支払いのためのクレジットカード情報をご入力ください。
          <br />
          入力が終わりましたら、「登録する」ボタンを押してください。
        </p>

        {!cardData ? (
          <Elements stripe={stripePromise}>
            <PaymentForm
              plan={
                selectedPlan === 'standard'
                  ? 'standardMonth'
                  : selectedPlan === 'self'
                  ? 'selfMonth'
                  : selectedPlan === 'liteMonth'
                  ? 'liteMonth'
                  : selectedPlan === 'liteYear'
                  ? 'liteYear'
                  : 'selfMonth'
              }
              email={email}
              storeId={staff?.storeId}
              shopId={staff?.shopId}
              setIsDone={setIsDone}
              setFailedMsg={setFailedMsg}
              setIsModal={setIsModal}
              handleSignOut={handleSignOut}
            />
          </Elements>
        ) : (
          <div className={styles.registerplan_existing_card}>
            <div className={styles.registerplan_card_info}>
              <label className={styles.registerplan_card_label}>
                カード情報
              </label>
              <div className={styles.registerplan_card_display}>
                <span>{cardData.split('|')[0]}</span>
                <span>{cardData.split('|')[1]}</span>
              </div>
            </div>
            <div className={styles.registerplan_submit_button}>
              <SubmitButton
                label="登録する"
                color="orange"
                size="semilarge"
                icon="none"
                onClick={() =>
                  apiCreateStripeSubscriptionDaseruno(
                    email,
                    cardData.split('|')[2],
                    selectedPlan === 'standard'
                      ? 'standardMonth'
                      : selectedPlan === 'self'
                      ? 'selfMonth'
                      : selectedPlan === 'liteMonth'
                      ? 'liteMonth'
                      : selectedPlan === 'liteYear'
                      ? 'liteYear'
                      : 'selfMonth',
                    cardData.split('|')[3],
                    staff?.shopId,
                    staff?.storeId,
                  )
                }
              />
            </div>
            <div className={styles.registerplan_logout_button}>
              <SubmitButton
                label="ログアウト"
                color="orange_outlined"
                size="semilarge"
                icon="none"
                onClick={handleSignOut}
              />
            </div>
          </div>
        )}

        <p className={styles.registerplan_note}>
          マイページ内「<span>ご利用プランの管理</span>
          」からお支払い方法の変更や領収書の発行ができます。
        </p>
      </div>
      {isDone && (
        <div className={styles.modal_account_front}>
          <div className={styles.modal_account_text}>
            プランの登録が完了しました
          </div>
          <div className={styles.modalButtonContainer}>
            <SubmitButton
              label="トップへ進む"
              color="orange"
              size="xmiddle"
              icon="none"
              onClick={() => {
                navigate('/')
                window.scrollTo(0, 0)
              }}
            />
          </div>
        </div>
      )}
      {!!failedMsg && (
        <div className={styles.modal_account}>
          <div
            className={styles.modal_account_text}
            style={{ color: '#ED7C7C' }}
          >
            プランの登録に失敗しました
          </div>
          <div
            className={styles.modal_account_sub_text}
            style={{ color: '#ED7C7C' }}
          >
            {failedMsg}
          </div>
          <div className={styles.modalButtonContainer}>
            <SubmitButton
              label="戻る"
              color="white"
              size="xmiddle"
              icon="none"
              onClick={() => {
                setFailedMsg('')
                window.location.reload()
              }}
            />
          </div>
        </div>
      )}
      {isModal && <CardCvcModal setIsModal={setIsModal} />}
    </div>
  )
}

type PaymentFormProps = {
  plan: 'liteMonth' | 'liteYear' | 'standardMonth' | 'selfMonth'
  email: string
  storeId?: string | null
  shopId?: string | null
  setIsDone: (e: boolean) => void
  setFailedMsg: (e: string) => void
  setIsModal: (e: boolean) => void
  handleSignOut: () => void
}
const PaymentForm: FC<PaymentFormProps> = ({
  plan,
  email,
  storeId,
  shopId,
  setIsDone,
  setFailedMsg,
  setIsModal,
  handleSignOut,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch<any>()
  const search = useLocation().search
  const query = new URLSearchParams(search)
  const handleSubmitPayment = async () => {
    if (!stripe || !elements || !storeId) return

    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement) return

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
    })

    if (error) {
      console.log('[error]', error)
    } else {
      dispatch(setIsLoading({ isLoading: true }))
      /** Subscription作成(新規カード) */
      const isDebug =
        process.env.REACT_APP_NODE_ENV === 'development' && !!query.get('debug')
      subscriptionsApi
        .createSubscription({
          email,
          paymentMethodId: paymentMethod.id,
          plan,
          shopId,
          storeId,
          isStandard:
            plan === 'standardMonth' || plan === 'selfMonth' ? true : false,
          isDebug,
        })
        .then((result: any) => {
          console.log(result.data)
          setIsDone(true)
        })
        .catch((error) => {
          console.log('Full error object:', error)
          console.log('Error code:', error.code)
          console.log('Error message:', error.message)
          console.log('Error details:', error.details)
          setFailedMsg(error.message)
        })
        .finally(() => dispatch(setIsLoading({ isLoading: false })))
    }
  }
  return (
    <div className={styles.registerplan_payment_form}>
      <div className={styles.registerplan_form_group}>
        <label className={styles.registerplan_form_label}>カード番号</label>
        <div className={styles.registerplan_form_input}>
          <CardNumberElement />
        </div>
      </div>

      <div className={styles.registerplan_form_row}>
        <div className={styles.registerplan_form_group}>
          <label className={styles.registerplan_form_label}>有効期限</label>
          <div className={styles.registerplan_form_input}>
            <CardExpiryElement />
          </div>
        </div>

        <div className={styles.registerplan_form_group}>
          <label className={styles.registerplan_form_label}>
            セキュリティコード
          </label>
          <div className={styles.registerplan_form_input}>
            <CardCvcElement />
          </div>
          <button
            type="button"
            className={styles.registerplan_security_link}
            onClick={() => setIsModal(true)}
          >
            セキュリティコードとは
          </button>
        </div>
      </div>

      <div className={styles.registerplan_submit_button}>
        <SubmitButton
          label="登録する"
          color="orange"
          size="large"
          icon="none"
          onClick={handleSubmitPayment}
        />
      </div>
      <div className={styles.registerplan_logout_button}>
        <SubmitButton
          label="ログアウト"
          color="orange_outlined"
          size="large"
          icon="none"
          onClick={handleSignOut}
        />
      </div>
    </div>
  )
}

type CardCvcModalProps = {
  setIsModal: (e: boolean) => void
}
export const CardCvcModal: FC<CardCvcModalProps> = ({ setIsModal }) => {
  return (
    <div className={styles.modal_card_security_code_account}>
      <CardSecurityCode width={400} />
      <div className={styles.modal_card_security_code_account_text}>
        ※ JCB・Visa・Mastercardの場合、
        <span style={{ color: '#F3616F' }}>カードの裏面に3桁の数字</span>
        でセキュリティコードが記載されています。裏面署名欄の右上をご確認ください。
      </div>
      <div className={styles.modal_card_security_code_account_text}>
        ※ American Expressの場合、
        <span style={{ color: '#F3616F' }}>カードの表面に4桁の数字</span>
        で記載されています。主にカード番号の右上に印刷されています。
      </div>
      <div className={styles.modalButtonContainer}>
        <SubmitButton
          label="閉じる"
          color="orange"
          size="xmiddle"
          icon="none"
          onClick={() => setIsModal(false)}
        />
      </div>
    </div>
  )
}

export default RegisterPlanTemplate
